import { ACCEPT_DECIMAL, formatPercentageNatural, FormatRegistry } from '@packages/core';
import {
  coerceFormDateValue,
  coerceFormNumberValue,
  coerceSelectString,
  isMeaningfulFormValue,
  parseFiniteNumber,
} from '@packages/shared/utils';

export type VestingSummaryLine = { highlight: string; rest: string };
export type VestingSummaryVariant = 'cliff' | 'step' | 'hybrid' | 'custom' | 'error';

export interface VestingSummaryData {
  visible: boolean;
  variant: VestingSummaryVariant | null;
  lines: VestingSummaryLine[];
  errorMessage?: string;
}

export interface BuildVestingSummaryOptions {
  fieldErrors?: Record<string, unknown>;
  acceptDecimal?: number;
}

export const VESTING_FREQUENCY_OPTIONS = [
  { label: 'Monthly', value: 1 },
  { label: 'Quarterly', value: 3 },
  { label: 'Semi-Annually', value: 6 },
  { label: 'Annually', value: 12 },
] as const;

/** Legacy newVestingModal: footer Edit only when list row `isEditable` is true. */
export function isVestingTemplateRowEditable(row: Record<string, unknown> | undefined): boolean {
  const flag = row?.isEditable;
  return flag === true || flag === 'true';
}

function hasFieldError(fieldErrors: Record<string, unknown> | undefined, field: string): boolean {
  const err = fieldErrors?.[field];
  return err != null && err !== false && String(err).length > 0;
}

function monthSuffix(count: number): string {
  return `${count} month${count > 1 ? 's' : ''}`;
}

export function filterVestingFrequencyOptions(
  lengthInMonths: unknown,
  vestsAfterInMonths: unknown,
): Array<{ label: string; value: number }> {
  const length = parseFiniteNumber(lengthInMonths);
  if (length == null || length <= 0) return [];

  const vestsAfter = parseFiniteNumber(vestsAfterInMonths) ?? 0;
  const remainder = length - vestsAfter;

  return VESTING_FREQUENCY_OPTIONS.filter(
    (item) => remainder % Number(item.value) === 0,
  ).map((item) => ({ label: item.label, value: item.value }));
}

/** Interval >= term — used by custom validator + summary error state. */
export function isIntervalAtLeastTerm(
  intervalInMonths: unknown,
  lengthInMonths: unknown,
): boolean {
  const length = parseFiniteNumber(lengthInMonths);
  const interval = parseFiniteNumber(intervalInMonths);
  if (length == null || interval == null) return false;
  return interval >= length;
}

/** Interval does not divide term evenly (when interval is in (0, term)). */
export function isIntervalNotDivisibleByTerm(
  intervalInMonths: unknown,
  lengthInMonths: unknown,
): boolean {
  if (!isMeaningfulFormValue(intervalInMonths)) return false;
  const length = parseFiniteNumber(lengthInMonths);
  const interval = parseFiniteNumber(intervalInMonths);
  if (length == null || interval == null || length <= 0) return false;
  if (interval <= 0 || interval >= length) return false;
  return length % interval !== 0;
}

/** Shared by form validators and summary callout — interval must be < term and divide evenly. */
export function isStepIntervalInvalid(
  intervalInMonths: unknown,
  lengthInMonths: unknown,
): boolean {
  return (
    isIntervalAtLeastTerm(intervalInMonths, lengthInMonths) ||
    isIntervalNotDivisibleByTerm(intervalInMonths, lengthInMonths)
  );
}

/** Shared by form validators and summary callout — hybrid cliffs must be < term. */
export function isHybridCliffsInvalid(
  vestsAfterInMonths: unknown,
  lengthInMonths: unknown,
): boolean {
  if (!isMeaningfulFormValue(vestsAfterInMonths)) return false;
  const length = parseFiniteNumber(lengthInMonths);
  const cliffs = parseFiniteNumber(vestsAfterInMonths);
  if (length == null || cliffs == null || length <= 0) return false;
  return cliffs >= length;
}

/** Legacy handlePercentageOne — hybrid first-line %. */
export function computePercentageOne(value: unknown, lengthInMonths: number): number {
  if (value == null || Number(value) <= 0) return 0;
  return (Number(value) / Number(lengthInMonths)) * 100;
}

/** Legacy handlePercentageTwo — step vesting interval %. */
export function computePercentageTwo(value: unknown, lengthInMonths: number): number {
  if (value == null || Number(value) <= 0) return 0;
  return (Number(value) / Number(lengthInMonths)) * 100;
}

/** Legacy handlePercentageThree — hybrid thereafter interval %. */
export function computePercentageThree(
  intervalInMonths: number,
  lengthInMonths: number,
  amountOneInPercentage: number,
  vestsAfterInMonths: number,
): number {
  const remainingPercentage = 100 - Number(amountOneInPercentage);
  const remainingMonth = Number(lengthInMonths) - Number(vestsAfterInMonths);
  const newValue = remainingPercentage / remainingMonth;

  const quarterlyRemainingMonth = remainingMonth / 3;
  const quarterlyValue = remainingPercentage / quarterlyRemainingMonth;
  const semiAnnuallyRemainingMonth = remainingMonth / 6;
  const semiAnnuallyValue = remainingPercentage / semiAnnuallyRemainingMonth;
  const annuallyRemainingMonth = remainingMonth / 12;
  const annuallyValue = remainingPercentage / annuallyRemainingMonth;

  if (amountOneInPercentage) {
    if (intervalInMonths === 3) return quarterlyValue;
    if (intervalInMonths === 6) return semiAnnuallyValue;
    if (intervalInMonths === 12) return annuallyValue;
    return newValue;
  }

  return (Number(intervalInMonths) / Number(lengthInMonths)) * 100;
}

function computeVestModulus(values: Record<string, unknown>): number | null {
  const termPeriod = parseFiniteNumber(values.lengthInMonths);
  const cliff = parseFiniteNumber(values.vestsAfterInMonths);
  const stepSize = parseFiniteNumber(values.intervalInMonths);
  if (termPeriod == null || cliff == null || stepSize == null) return null;
  const remainingDuration = termPeriod - cliff;
  if (!remainingDuration || !stepSize) return null;
  return remainingDuration % stepSize;
}

function formatPeriodStartRest(periodStartDate: unknown): string {
  if (periodStartDate == null || periodStartDate === '' || periodStartDate === 'Invalid date') {
    return 'vesting start date';
  }
  const formatted = FormatRegistry.get('DATE_MDY')?.(periodStartDate);
  return formatted && formatted !== '-' ? ` on ${formatted}` : 'vesting start date';
}

const INVALID_MESSAGE = 'Invalid Data. Please try again.';

function errorResult(): VestingSummaryData {
  return {
    visible: true,
    variant: 'error',
    lines: [],
    errorMessage: INVALID_MESSAGE,
  };
}

function successResult(
  variant: VestingSummaryVariant,
  lines: VestingSummaryLine[],
): VestingSummaryData {
  return {
    visible: lines.length > 0,
    variant,
    lines,
  };
}

/**
 * Builds reactive summary callout data from current form values.
 * Ports visibility + copy from legacy newVestingModal.tsx ViewUploadedDoc blocks.
 */
export function buildVestingSummaryData(
  values: Record<string, unknown>,
  options?: BuildVestingSummaryOptions,
): VestingSummaryData {
  const decimals = options?.acceptDecimal ?? ACCEPT_DECIMAL;
  const fieldErrors = options?.fieldErrors;

  const isCliff = values.isCliffVesting === true;
  const isStep = values.isStepVesting === true;
  const isHybrid = values.isHybridVesting === true;

  const lengthInMonths = parseFiniteNumber(values.lengthInMonths);
  const intervalInMonths = parseFiniteNumber(values.intervalInMonths);
  const vestsAfterInMonths = parseFiniteNumber(values.vestsAfterInMonths);
  const amountOneInPercentage = parseFiniteNumber(values.amountOneInPercentage);

  const vestModulus = computeVestModulus(values);
  if (vestModulus != null && vestModulus < 0) {
    return errorResult();
  }

  if (
    !isCliff &&
    lengthInMonths != null &&
    lengthInMonths > 0 &&
    isMeaningfulFormValue(values.vestsAfterInMonths) &&
    hasFieldError(fieldErrors, 'vestsAfterInMonths')
  ) {
    return errorResult();
  }

  if (
    isStep &&
    lengthInMonths != null &&
    lengthInMonths > 0 &&
    isMeaningfulFormValue(values.intervalInMonths) &&
    (hasFieldError(fieldErrors, 'intervalInMonths') ||
      isStepIntervalInvalid(values.intervalInMonths, values.lengthInMonths))
  ) {
    return errorResult();
  }

  if (
    isCliff &&
    isMeaningfulFormValue(values.lengthInMonths) &&
    hasFieldError(fieldErrors, 'lengthInMonths')
  ) {
    return errorResult();
  }

  if (
    isCliff &&
    lengthInMonths != null &&
    lengthInMonths > 0 &&
    !hasFieldError(fieldErrors, 'lengthInMonths')
  ) {
    return successResult('cliff', [
      {
        highlight: '100%',
        rest: `of shares will vest after ${monthSuffix(lengthInMonths)}`,
      },
    ]);
  }

  if (
    isStep &&
    lengthInMonths != null &&
    lengthInMonths > 0 &&
    intervalInMonths != null &&
    intervalInMonths >= 0 &&
    !hasFieldError(fieldErrors, 'intervalInMonths') &&
    !isStepIntervalInvalid(values.intervalInMonths, values.lengthInMonths)
  ) {
    const pct = computePercentageTwo(intervalInMonths, lengthInMonths);
    return successResult('step', [
      {
        highlight: formatPercentageNatural(pct, decimals),
        rest: `vest every ${monthSuffix(intervalInMonths)}`,
      },
    ]);
  }

  if (
    isHybrid &&
    lengthInMonths != null &&
    lengthInMonths > 0 &&
    isMeaningfulFormValue(values.vestsAfterInMonths) &&
    (hasFieldError(fieldErrors, 'vestsAfterInMonths') ||
      isHybridCliffsInvalid(values.vestsAfterInMonths, values.lengthInMonths))
  ) {
    return errorResult();
  }

  if (
    isHybrid &&
    lengthInMonths != null &&
    lengthInMonths > 0 &&
    vestsAfterInMonths != null &&
    vestsAfterInMonths >= 0 &&
    !hasFieldError(fieldErrors, 'vestsAfterInMonths') &&
    !isHybridCliffsInvalid(values.vestsAfterInMonths, values.lengthInMonths)
  ) {
    const firstPct =
      amountOneInPercentage != null && amountOneInPercentage > 0
        ? formatPercentageNatural(amountOneInPercentage, decimals)
        : formatPercentageNatural(computePercentageOne(vestsAfterInMonths, lengthInMonths), decimals);

    const lines: VestingSummaryLine[] = [
      {
        highlight: firstPct,
        rest: `vest after the first ${monthSuffix(vestsAfterInMonths)}`,
      },
    ];

    if (isMeaningfulFormValue(values.intervalInMonths) && intervalInMonths != null) {
      const pct = computePercentageThree(
        intervalInMonths,
        lengthInMonths,
        amountOneInPercentage ?? computePercentageOne(vestsAfterInMonths, lengthInMonths),
        vestsAfterInMonths,
      );
      lines.push({
        highlight: formatPercentageNatural(pct, decimals),
        rest: `vest every ${monthSuffix(intervalInMonths)} thereafter`,
      });
    }

    return successResult('hybrid', lines);
  }

  if (
    !isCliff &&
    !isStep &&
    !isHybrid &&
    lengthInMonths != null &&
    lengthInMonths > 0 &&
    amountOneInPercentage != null &&
    amountOneInPercentage > 0 &&
    isMeaningfulFormValue(values.intervalInMonths)
  ) {
    const vestingTypeLabel = String(values.vestingTypeLabel ?? '');
    const lines: VestingSummaryLine[] = [];

    if (intervalInMonths != null && vestingTypeLabel !== 'Custom Vesting') {
      lines.push({
        highlight: `${values.amountOneInPercentage}%`,
        rest: formatPeriodStartRest(values.periodStartDate),
      });
    }

    if (intervalInMonths != null) {
      lines.push({
        highlight: '',
        rest: `Vest every ${monthSuffix(intervalInMonths)} thereafter`,
      });
    }

    return successResult('custom', lines.filter((l) => l.highlight || l.rest));
  }

  return {
    visible: false,
    variant: null,
    lines: [],
  };
}

// ─── Legacy parity mappers (newVestingModal.tsx handleSubmit + initialValues) ───

export type VestingTypeOptionMeta = {
  label: string;
  value: string;
  isCliffVesting?: boolean;
  isStepVesting?: boolean;
  isHybridVesting?: boolean;
};

/** API DTO → form values (legacy initialValues + spread). */
export function mapVestingTemplateApiToForm(
  raw: Record<string, unknown>,
  id: string,
  selectedType?: VestingTypeOptionMeta,
  acceptDecimal: number = ACCEPT_DECIMAL,
): Record<string, unknown> {
  const vestingTypeId = coerceSelectString(raw.vestingTypeId);
  const vestingTypeLabel =
    selectedType?.label ?? (raw.vestingType != null ? String(raw.vestingType) : '');

  let amountOneInPercentage: number | '' = '';
  if (raw.amountOneInPercentage != null && raw.amountOneInPercentage !== '') {
    amountOneInPercentage = Number(
      Number(Number(raw.amountOneInPercentage) * 100).toFixed(acceptDecimal),
    );
  }

  return {
    id: coerceSelectString(raw.id ?? id),
    name: raw.name != null ? String(raw.name) : '',
    summary: raw.summary === null || raw.summary === undefined ? '' : String(raw.summary),
    vestingTypeId,
    vestingType: vestingTypeLabel,
    vestingTypeLabel,
    isCliffVesting: selectedType?.isCliffVesting ?? false,
    isStepVesting: selectedType?.isStepVesting ?? false,
    isHybridVesting: selectedType?.isHybridVesting ?? false,
    loadingTypeId: coerceSelectString(raw.loadingTypeId),
    endOfTheMonthTypeId: coerceSelectString(raw.endOfTheMonthTypeId),
    roundingTypeId: coerceSelectString(raw.roundingTypeId),
    specificVestingInstruction:
      raw.specificVestingInstruction != null ? String(raw.specificVestingInstruction) : '',
    periodStartDate: coerceFormDateValue(raw.periodStartDate),
    lengthInMonths: coerceFormNumberValue(raw.lengthInMonths),
    vestsAfterInMonths: coerceFormNumberValue(raw.vestsAfterInMonths),
    intervalInMonths: coerceFormNumberValue(raw.intervalInMonths),
    vestsInMonths: coerceFormNumberValue(raw.vestsInMonths),
    amountOneInPercentage,
    vestAtEndOfMonth: Boolean(raw.vestAtEndOfMonth),
    fractionalDecimalDigit: Number(raw.fractionalDecimalDigit ?? 0),
  };
}

/** Form values → API DTO (legacy handleSubmit payload shape). */
export function mapVestingTemplateFormToApi(
  payload: Record<string, unknown>,
  selectedType?: VestingTypeOptionMeta,
): Record<string, unknown> {
  const vestingTypeLabel = selectedType?.label ?? null;

  const isTranche = vestingTypeLabel === 'Custom Vesting Tranche';
  const isSpecific = vestingTypeLabel === 'Specific Vesting';
  const isCliff = selectedType?.isCliffVesting === true;
  const lengthRaw = isTranche || isSpecific ? 1 : payload.lengthInMonths;
  const intervalPayload = payload.intervalInMonths;
  const intervalRaw =
    isTranche || isSpecific
      ? intervalPayload != null && intervalPayload !== ''
        ? intervalPayload
        : 1
      : intervalPayload;
  const vestsAfterRaw = payload.vestsAfterInMonths;

  const cliffTerm =
    isCliff && lengthRaw != null && lengthRaw !== '' && !Number.isNaN(Number(lengthRaw))
      ? Number(lengthRaw)
      : null;

  let intervalForApi = Number(intervalRaw);
  let vestsAfterForApi =
    vestsAfterRaw != null && vestsAfterRaw !== '' ? Number(vestsAfterRaw) : null;
  let amountOneForApi =
    payload.amountOneInPercentage !== '' && payload.amountOneInPercentage != null
      ? payload.amountOneInPercentage
      : null;

  // Legacy newVestingModal: on Term Period change for cliff → interval 1, cliffs = term, 100%.
  if (cliffTerm != null && cliffTerm > 0) {
    intervalForApi = 1;
    vestsAfterForApi = cliffTerm;
    amountOneForApi = 100;
  }

  // Legacy handleSubmit: Number(lengthInMonths) - Number(vestsAfterInMonths); empty cliffs → 0.
  const vestsAfterForCalc =
    vestsAfterForApi != null ? vestsAfterForApi : Number(vestsAfterRaw ?? 0);
  const vestsInMonths =
    lengthRaw != null && lengthRaw !== '' && !Number.isNaN(Number(lengthRaw))
      ? Number(lengthRaw) - vestsAfterForCalc
      : 0;

  const result: Record<string, unknown> = {
    intervalInMonths: intervalForApi,
    amountOneInPercentage: amountOneForApi,
    lengthInMonths:
      vestingTypeLabel === 'Specific Vesting'
        ? '1'
        : lengthRaw != null && lengthRaw !== ''
          ? Number(lengthRaw)
          : null,
    name: payload.name,
    summary: payload.summary ?? null,
    vestingTypeId: payload.vestingTypeId ?? null,
    vestingType: vestingTypeLabel,
    vestsAfterInMonths: vestsAfterForApi,
    roundingTypeId: selectedType?.isCliffVesting
      ? 3
      : isTranche
        ? 1
        : payload.roundingTypeId ?? null,
    specificVestingInstruction:
      vestingTypeLabel === 'Specific Vesting' ? payload.specificVestingInstruction : null,
    periodStartDate: payload.periodStartDate ? payload.periodStartDate : null,
    vestAtEndOfMonth: payload.vestAtEndOfMonth ?? false,
    vestsInMonths: vestsInMonths ?? 0,
    fractionalDecimalDigit: 0,
    loadingTypeId: isMeaningfulFormValue(payload.loadingTypeId) ? payload.loadingTypeId : null,
    endOfTheMonthTypeId: isMeaningfulFormValue(payload.endOfTheMonthTypeId)
      ? payload.endOfTheMonthTypeId
      : 1,
  };

  if (payload.id) {
    result.id = payload.id;
  }

  return result;
}
